import {differenceInDays, parseISO} from 'date-fns'

import {hasAnchorPrice} from '@daedalus/core/src/offer/business/price'
import {TaxDisplayLogic} from '@daedalus/core/src/offer/types/TaxDisplayLogic'
import {sortOffersByPrice} from '@daedalus/core/src/offer/utils/sortOffers'
import {
  toDecreasePercentage,
  toFloatWithTwoDecimals
} from '@daedalus/core/src/utils/number'
import {providersMap, ProvidersMapKeys} from '@daedalus/core/src/utils/provider'

import {CancellationPenalty, Offer, Rate} from '../../types/partnersApi/types'

interface GetNightlyPriceParams {
  rate: Rate
  numberOfNights: number
  numberOfRooms: number
  taxDisplayLogic: TaxDisplayLogic
}

export const hasFreeCancellationPenalty = (
  cancellationPenalties?: CancellationPenalty[]
) =>
  cancellationPenalties &&
  cancellationPenalties?.length > 0 &&
  !cancellationPenalties[0]?.amount

export const getProviderCodeFromName = (value: string) => {
  const keys = Object.keys(providersMap) as ProvidersMapKeys[]

  return (
    keys.find(
      key =>
        providersMap[key].split('.')[0].toLowerCase() ===
        value.split('.')[0].toLowerCase()
    ) ?? 'VIO'
  )
}

export const calculateNightlyPrice = (
  price: number,
  numberOfNights: number,
  numberOfRooms: number
) => {
  if (Number.isNaN(price)) return null

  return toFloatWithTwoDecimals(price / numberOfNights / numberOfRooms)
}

const calculateDisplayTotal = ({
  rate,
  numberOfNights,
  numberOfRooms,
  taxDisplayLogic
}: GetNightlyPriceParams) => {
  const {includeTaxes, includeLocalTaxes} = taxDisplayLogic
  let totalRate = rate.base
  const {taxes, hotelFees} = rate
  if (includeTaxes && rate) {
    totalRate += taxes
  }

  if (includeLocalTaxes && hotelFees) {
    totalRate += hotelFees
  }

  return calculateNightlyPrice(totalRate, numberOfNights, numberOfRooms)
}

export const getNightlyDisplayTotalPrice = ({
  rate,
  numberOfNights,
  numberOfRooms,
  taxDisplayLogic
}: GetNightlyPriceParams) =>
  calculateDisplayTotal({rate, taxDisplayLogic, numberOfNights, numberOfRooms})

export const getRoundedNightlyDisplayTotalPrice = (
  params: GetNightlyPriceParams
) => Math.round(Number(getNightlyDisplayTotalPrice(params)))

interface GetOfferWithCheapestNightlyPriceParams {
  offers: Offer[]
  numberOfNights: number
  numberOfRooms: number
  taxDisplayLogic: TaxDisplayLogic
}

export const getOfferWithCheapestNightlyPrice = ({
  offers,
  numberOfNights,
  numberOfRooms,
  taxDisplayLogic
}: GetOfferWithCheapestNightlyPriceParams) => {
  // eslint-disable-next-line fp/no-mutating-methods
  return [...offers].sort((offerA, offerB) => {
    const nightlyPriceA = getRoundedNightlyDisplayTotalPrice({
      rate: offerA.rate,
      numberOfNights,
      numberOfRooms,
      taxDisplayLogic
    })

    const nightlyPriceB = getRoundedNightlyDisplayTotalPrice({
      rate: offerB.rate,
      numberOfNights,
      numberOfRooms,
      taxDisplayLogic
    })

    return nightlyPriceA - nightlyPriceB
  })[0]
}

export const getNumberOfNights = (checkIn: string, checkOut: string) =>
  differenceInDays(parseISO(checkOut), parseISO(checkIn))

export const getDisplayDiscount = (price: number, anchorPrice?: number) => {
  if (!anchorPrice) return undefined

  const savingPercentage = toDecreasePercentage(price, anchorPrice, 'floor')
  const hasDiscount = !!savingPercentage && hasAnchorPrice(price, anchorPrice)

  return hasDiscount ? savingPercentage : undefined
}

export const getAnchorRate = (offers: Offer[]): Rate => {
  const topThreeOffers = offers.slice(0, 3)
  const sortedOffers = sortOffersByPrice(topThreeOffers)
  const mostExpensiveOffer = sortedOffers[sortedOffers.length - 1]

  return mostExpensiveOffer.rate
}
